import { Icon, Logo, Logomark } from "@platform/ui/components";
import { useSuspenseQuery } from "@tanstack/react-query";
import * as React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";
import { getRouteTreeRoot } from "~/shared/api/users/users.helpers";
import { USERS_QUERIES } from "~/shared/api/users/users.queries";
import { useSidebarNavState } from "~/shared/components/PrivateAppLayout/context/SidebarNavContext";
import { navAction } from "~/shared/styles/layout.styles";

interface SidebarNavProps {
  children?: React.ReactNode;
}
export function SidebarNav({ children }: SidebarNavProps) {
  const { isOpen, setIsOpen } = useSidebarNavState() ?? {};
  const { container } = navAction({
    state: isOpen ? "expanded" : "collapsed",
  });

  return (
    <div
      data-testid="sidebar-nav"
      className="border-ds-stroke-primary bg-ds-surface-1 flex h-screen w-full flex-col border-r border-solid"
    >
      <header
        className={twMerge(
          "flex flex-wrap items-center justify-between gap-5",
          isOpen ? "h-(--siteheader-height) px-6" : "justify-center px-5 py-6",
        )}
      >
        <div className={twMerge("w-full", isOpen ? "max-w-[150px]" : "max-w-[30px]")}>
          <ErrorBoundary fallback={<LogoLinkContent isOpen={isOpen === true} />}>
            <React.Suspense fallback={<LogoLinkContent isOpen={isOpen === true} />}>
              <LogoLink>
                <LogoLinkContent isOpen={isOpen === true} />
              </LogoLink>
            </React.Suspense>
          </ErrorBoundary>
        </div>
        <button
          className={container({
            state: "collapsed",
            className: "hidden h-10 w-10 place-items-center [@media(min-width:1281px)]:grid",
          })}
          aria-label={`${isOpen ? "Collapse" : "Expand"} Sidebar Navigation`}
          onClick={() => setIsOpen?.((prev) => !prev)}
        >
          <Icon
            className="text-ds-icon-primary"
            aria-hidden
            symbol={isOpen ? "sidebar-fold-line" : "sidebar-unfold-line"}
          />
        </button>
      </header>
      <div className="flex min-h-0 flex-1">{children}</div>
    </div>
  );
}

function LogoLink({ children }: { children?: React.ReactNode }) {
  const { data: user } = useSuspenseQuery(USERS_QUERIES.profile());
  const routeTreeRoot = getRouteTreeRoot(user.type);

  return (
    <Link to={routeTreeRoot} aria-label="Go to homepage" className="block w-full">
      {children}
    </Link>
  );
}

function LogoLinkContent({ isOpen }: { isOpen: boolean }) {
  return isOpen ? <Logo aria-hidden /> : <Logomark aria-hidden />;
}
